import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { InputField } from "@/components/input-field/InputField";
import {
  AsyncAutocompleteSelect,
  Option,
} from "@/components/select/AsyncAutocompleteSelect";
import { AppRoutes } from "@/constants/routes";
import { useRoles } from "@/hooks/useRoles";
import { ActiveDirectoryTechnicianUser } from "@/services/technicians-ad.service";
import { AppColors } from "@/styles/colors";
import { isEmpty } from "@/utils/query-string";
import { Card, Field, Flex, Grid, GridItem, Text } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { technicianCreationSchema } from "../schema/technicians.schema";
import {
  fetchDefaultADUsersHandler,
  getADOptionsHandler,
  handleUserSelectionHandler,
  loadADOptionsHandler,
  submitCreateTechnicianHandler,
} from "../schema/handler-functions";

type TechnicianCreationForm = {
  usuario?: Option | null;
  nombreApellido?: string;
  email?: string;
};

export const TechnicianCreationPage = () => {
  const navigate = useNavigate();
  const { fetchPermissions } = useRoles();

  const [isActive, setIsActive] = useState(false);
  useEffect(() => {
    setIsActive(true);
  }, []);

  const { control, setValue, handleSubmit } = useForm<TechnicianCreationForm>({
    resolver: zodResolver(technicianCreationSchema),
    defaultValues: {
      usuario: null,
      nombreApellido: "",
      email: "",
    },
  });

  const watched = useWatch({ control });

  const [loadingCreate, setLoadingCreate] = useState(false);

  const [loadingADUsers, setLoadingADUsers] = useState(false);
  const [defaultADUsers, setDefaultADUsers] = useState<
    ActiveDirectoryTechnicianUser[]
  >([]);

  const adUserById = useMemo(() => {
    const map = new Map<string, ActiveDirectoryTechnicianUser>();
    for (const u of defaultADUsers ?? []) {
      if (u?.id) map.set(String(u.id), u);
    }
    return map;
  }, [defaultADUsers]);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadADOptions = (inputValue: string): Promise<Option[]> => {
    return loadADOptionsHandler({
      timeoutRef,
      inputValue,
      setLoadingADUsers,
      getADOptions: async (value) => getADOptionsHandler({ inputValue: value }),
    });
  };

  useEffect(() => {
    if (!isActive) return;
    fetchDefaultADUsersHandler({
      setLoadingADUsers,
      setDefaultADUsers,
    });
  }, [isActive]);

  const handleCancel = () => {
    navigate(AppRoutes.TECHNICIANS);
  };

  const hasNoFilledValues = useMemo(() => {
    const userId = watched?.usuario?.value;
    const name = watched?.nombreApellido;
    return !userId || isEmpty(name);
  }, [watched?.usuario?.value, watched?.nombreApellido]);

  const onSubmit = async (values: TechnicianCreationForm) => {
    await submitCreateTechnicianHandler({
      values,
      setLoadingCreate,
      navigate,
      fetchPermissions,
    });
  };

  const defaultADOptions = useMemo(() => {
    return (
      defaultADUsers?.map((u) => ({
        label: `${u.displayName} - ${u.mail}`,
        value: u.id,
      })) ?? []
    );
  }, [defaultADUsers]);

  return (
    <Flex
      flexDir="column"
      paddingTop="20px"
      gap="30px"
      height="100%"
      minHeight={0}
      data-testid="technicians-creation-page"
    >
      <SectionHeader
        title="Maestro de técnicos"
        addButtonLabel="Agregar"
        cancelButtonLabel="Cancelar"
        handleAdd={handleSubmit(onSubmit)}
        handleCancel={handleCancel}
        loading={loadingCreate}
        disableAdd={hasNoFilledValues || loadingCreate}
      />

      <Flex flex="1" minHeight={0} overflowY="auto" flexDir="column" gap="16px">
        <Card.Root maxW="full" borderRadius="8px">
          <Card.Body
            paddingX="48px"
            paddingY="30px"
            borderRadius="8px"
            border={`1px solid ${AppColors.WHITE_BORDER_1}`}
          >
            <Grid templateColumns="repeat(4, 1fr)" gap="32px">
              <GridItem colSpan={1}>
                <Field.Root>
                  <Field.Label>
                    <Text
                      fontWeight={600}
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                    >
                      Usuario (Active Directory)
                    </Text>
                  </Field.Label>
                  <AsyncAutocompleteSelect
                    control={control as any}
                    defaultOptions={defaultADOptions}
                    loadOptions={loadADOptions}
                    name="usuario"
                    placeholder="Seleccionar"
                    isLoading={loadingADUsers}
                    handleChange={(opt) =>
                      handleUserSelectionHandler({
                        opt,
                        adUserById,
                        setValue,
                      })
                    }
                    closeMenuOnSelect
                    triggerWithLength={false}
                    disabled={loadingCreate}
                    cacheOptions={false}
                  />
                </Field.Root>
              </GridItem>

              <GridItem colSpan={1}>
                <InputField
                  control={control as any}
                  inputName="nombreApellido"
                  labelText="Nombre"
                  placeholder="Ingresar"
                  required
                  disabled
                  minHeight="40px"
                />
              </GridItem>

              <GridItem colSpan={1}>
                <InputField
                  control={control as any}
                  inputName="email"
                  labelText="Email"
                  placeholder="Ingresar"
                  disabled
                  minHeight="40px"
                />
              </GridItem>
            </Grid>
          </Card.Body>
        </Card.Root>
      </Flex>
    </Flex>
  );
};
